import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MessageCircle, Send, Users, Wifi, WifiOff, Plus, Trash2, Zap, Bell, Image, X, FileText, Upload, CheckSquare, Square, Clock, CalendarClock, Power, Repeat } from "lucide-react";

const typeLabels = { silver: "Silver", gold: "Gold", platinum: "Platinum", prioritas: "Prioritas" };

function ImageAttach({ url, onUpload, onPaste, onRemove, uploading }) {
  if (url) return (
    <div className="flex items-center gap-3 bg-[#1A1A1A] border border-[#262626] p-2.5">
      <img src={url} alt="" className="w-14 h-14 object-cover border border-[#262626] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-green-400 font-semibold">Foto terlampir</p>
        <p className="text-[10px] text-gray-500 truncate">{url}</p>
      </div>
      <button type="button" onClick={onRemove} className="text-gray-500 hover:text-red-400 shrink-0 p-1"><X size={14} /></button>
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <label className="flex-1 flex items-center gap-2 cursor-pointer bg-[#1A1A1A] border border-dashed border-[#333] hover:border-green-700/50 p-2.5 transition-colors">
        <Upload size={14} className="text-gray-500 shrink-0" />
        <span className="text-xs text-gray-400">{uploading ? "Mengupload..." : "Upload foto (maks 4MB)"}</span>
        <input type="file" className="hidden" accept="image/*" onChange={e => onUpload(e.target.files[0])} disabled={uploading} />
      </label>
      <span className="text-gray-600 text-[10px]">atau</span>
      <Input className="flex-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none text-xs" placeholder="Tempel URL gambar..." onBlur={e => e.target.value && onPaste(e.target.value)} />
    </div>
  );
}

export default function WhatsAppPage() {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [sendForm, setSendForm] = useState({ target: "", message: "", url: "" });
  const [blastMsg, setBlastMsg] = useState("");
  const [blastUrl, setBlastUrl] = useState("");
  const [blastFilter, setBlastFilter] = useState("all");
  const [blastCustom, setBlastCustom] = useState({});
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", message: "", image_url: "" });
  const [scheduled, setScheduled] = useState([]);
  const [schedDialog, setSchedDialog] = useState(false);
  const [schedForm, setSchedForm] = useState({ name: "", message: "", image_url: "", target_filter: "all", schedule_type: "once", schedule_date: "", schedule_time_val: "10:00" });

  const fetchData = useCallback(async () => {
    try {
      const [dRes, tRes, cRes, nRes, sRes] = await Promise.all([api.get("/whatsapp/device"), api.get("/whatsapp/templates"), api.get("/customers"), api.get("/whatsapp/notifications?limit=30"), api.get("/whatsapp/scheduled")]);
      setDevice(dRes.data);
      setTemplates(tRes.data);
      setCustomers(cRes.data.filter(c => c.phone));
      setNotifications(nRes.data);
      setScheduled(sRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (file, cb) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Maks 4MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(data.url);
      toast.success("Foto diupload");
    } catch { toast.error("Upload gagal"); }
    finally { setUploading(false); }
  };

  const sendMessage = async () => {
    if (!sendForm.target || !sendForm.message) return toast.error("Target dan pesan harus diisi");
    setSending(true);
    try {
      await api.post("/whatsapp/send", sendForm);
      toast.success("Pesan terkirim!");
      setSendForm({ target: "", message: "", url: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };

  const getBlastTargets = () => {
    if (blastFilter === "all") return customers.map(c => c.phone);
    if (blastFilter === "custom") return Object.entries(blastCustom).filter(([,v]) => v).map(([k]) => k);
    return customers.filter(c => c.user_type === blastFilter).map(c => c.phone);
  };

  const sendBlast = async () => {
    if (!blastMsg) return toast.error("Pesan harus diisi");
    const targets = getBlastTargets();
    if (targets.length === 0) return toast.error("Tidak ada target terpilih");
    if (!window.confirm(`Kirim pesan ke ${targets.length} nomor?`)) return;
    setSending(true);
    try {
      const payload = { targets, message: blastMsg, delay: 2 };
      if (blastUrl) payload.url = blastUrl;
      const { data } = await api.post("/whatsapp/blast", payload);
      toast.success(`Blast selesai: ${data.sent}/${data.total} terkirim`);
      setBlastMsg(""); setBlastUrl("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };

  const toggleCustom = (phone) => setBlastCustom(prev => ({ ...prev, [phone]: !prev[phone] }));
  const selectAllCustom = () => {
    const filtered = blastFilter !== "custom" ? customers : customers;
    const all = {};
    filtered.forEach(c => { all[c.phone] = true; });
    setBlastCustom(all);
    setBlastFilter("custom");
  };

  const saveTpl = async () => {
    if (!tplForm.name) return toast.error("Nama template wajib diisi");
    try {
      await api.post("/whatsapp/templates", tplForm);
      toast.success("Template disimpan");
      setTplDialog(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteTpl = async (id) => { await api.delete(`/whatsapp/templates/${id}`); fetchData(); };

  const applyTemplate = (tpl, target) => {
    if (target === "send") {
      setSendForm(f => ({ ...f, message: tpl.message, url: tpl.image_url || f.url }));
    } else if (target === "sched") {
      setSchedForm(f => ({ ...f, message: tpl.message, image_url: tpl.image_url || f.image_url }));
    } else {
      setBlastMsg(tpl.message);
      if (tpl.image_url) setBlastUrl(tpl.image_url);
    }
    toast.success(`Template "${tpl.name}" dimuat`);
  };

  const openSchedDialog = () => {
    const tomorrow = new Date(Date.now() + 86400000);
    setSchedForm({ name: "", message: "", image_url: "", target_filter: "all", schedule_type: "once", schedule_date: tomorrow.toISOString().slice(0, 10), schedule_time_val: "10:00" });
    setSchedDialog(true);
  };

  const saveScheduled = async () => {
    if (!schedForm.message) return toast.error("Pesan wajib diisi");
    if (!schedForm.schedule_date || !schedForm.schedule_time_val) return toast.error("Tanggal dan jam wajib diisi");
    const isoTime = `${schedForm.schedule_date}T${schedForm.schedule_time_val}:00+07:00`;
    setSending(true);
    try {
      await api.post("/whatsapp/scheduled", {
        name: schedForm.name || `Jadwal ${schedForm.schedule_type}`,
        message: schedForm.message,
        image_url: schedForm.image_url,
        target_filter: schedForm.target_filter,
        targets: [],
        schedule_type: schedForm.schedule_type,
        schedule_time: isoTime,
      });
      toast.success("Jadwal berhasil dibuat");
      setSchedDialog(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const toggleScheduled = async (sid) => {
    try { await api.put(`/whatsapp/scheduled/${sid}/toggle`); fetchData(); } catch {}
  };

  const deleteScheduled = async (sid) => {
    if (!window.confirm("Hapus jadwal ini?")) return;
    await api.delete(`/whatsapp/scheduled/${sid}`);
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  const isConnected = device?.status !== false && (device?.device_status === "connect" || device?.status === "connect" || device?.device === true);
  const notConfigured = device?.status === false && (device?.message?.includes("belum dikonfigurasi") || !device?.device);
  const blastTargets = getBlastTargets();

  return (
    <div className="p-6 md:p-8" data-testid="whatsapp-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">WhatsApp Blast</h1>
        <p className="text-gray-400 text-sm mt-1">Kirim pesan & gambar WhatsApp ke pelanggan via Fonnte</p>
      </div>

      {/* Device Status */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6" data-testid="wa-device-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {notConfigured ? <WifiOff size={20} className="text-yellow-400" /> : isConnected ? <Wifi size={20} className="text-green-400" /> : <WifiOff size={20} className="text-red-400" />}
            <div>
              <p className="text-white font-semibold">Status Device</p>
              <p className="text-xs text-gray-400">{notConfigured ? "API Key belum dikonfigurasi" : isConnected ? "Terhubung" : "Tidak terhubung"}</p>
            </div>
          </div>
          <Badge className={notConfigured ? "bg-yellow-900/30 text-yellow-400" : isConnected ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>
            {notConfigured ? "Belum Dikonfigurasi" : isConnected ? "Connected" : "Disconnected"}
          </Badge>
        </div>
        {!notConfigured && device && (
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#262626]">
            <div><p className="text-xs text-gray-500">Device</p><p className="text-white text-sm">{device.device || device.name || device.device_name || "-"}</p></div>
            <div><p className="text-xs text-gray-500">Quota</p><p className="text-white text-sm">{device.quota || device.messages || "-"}</p></div>
            <div><p className="text-xs text-gray-500">Expired</p><p className="text-white text-sm">{device.expired || "-"}</p></div>
          </div>
        )}
        {notConfigured && <p className="text-xs text-yellow-400 mt-3">Buka Pengaturan untuk mengonfigurasi Fonnte API Key.</p>}
      </div>

      <Tabs defaultValue="send" className="w-full">
        <TabsList className="bg-[#0F0F0F] border border-[#262626] rounded-none h-10 w-full">
          <TabsTrigger value="send" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><Send size={14} className="mr-1" /> Kirim Pesan</TabsTrigger>
          <TabsTrigger value="blast" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><Zap size={14} className="mr-1" /> Blast</TabsTrigger>
          <TabsTrigger value="scheduled" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><CalendarClock size={14} className="mr-1" /> Jadwal ({scheduled.filter(s => s.is_active).length})</TabsTrigger>
          <TabsTrigger value="templates" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><MessageCircle size={14} className="mr-1" /> Template</TabsTrigger>
          <TabsTrigger value="log" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><Bell size={14} className="mr-1" /> Log ({notifications.length})</TabsTrigger>
        </TabsList>

        {/* === KIRIM PESAN === */}
        <TabsContent value="send">
          <div className="bg-[#0F0F0F] border border-[#262626] p-6 space-y-4" data-testid="wa-send-form">
            <div><Label className="text-gray-300 text-xs font-bold uppercase">No. WhatsApp Tujuan</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" placeholder="628xxxxxxxxxx" value={sendForm.target} onChange={e => setSendForm({...sendForm, target: e.target.value})} data-testid="wa-target-input" /></div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase">Pesan</Label>
              <textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[120px] text-sm focus:border-[#E60000] focus:outline-none" placeholder="Tulis pesan..." value={sendForm.message} onChange={e => setSendForm({...sendForm, message: e.target.value})} data-testid="wa-message-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-1.5 flex items-center gap-1"><Image size={12} /> Lampiran Gambar</Label>
              <ImageAttach url={sendForm.url} onUpload={f => handleUpload(f, u => setSendForm(p => ({...p, url: u})))} onPaste={u => setSendForm(p => ({...p, url: u}))} onRemove={() => setSendForm(p => ({...p, url: ""}))} uploading={uploading} />
            </div>
            {templates.length > 0 && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-[10px] text-gray-500 uppercase font-bold">Template:</span>
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t, "send")} className="text-[10px] px-2 py-1 border border-[#262626] text-gray-400 hover:text-[#E60000] hover:border-[#E60000] flex items-center gap-1">
                    {t.image_url && <Image size={10} className="text-green-500" />}{t.name}
                  </button>
                ))}
              </div>
            )}
            <Button onClick={sendMessage} disabled={sending || notConfigured} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="wa-send-btn">
              <Send size={14} className="mr-2" />{sending ? "Mengirim..." : sendForm.url ? "Kirim dengan Foto" : "Kirim Pesan"}
            </Button>
          </div>
        </TabsContent>

        {/* === BLAST === */}
        <TabsContent value="blast">
          <div className="bg-[#0F0F0F] border border-[#262626] p-6 space-y-4" data-testid="wa-blast-form">
            {/* Target Filter */}
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-2 block">Target Penerima</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { value: "all", label: `Semua (${customers.length})` },
                  ...Object.entries(typeLabels).map(([k, v]) => ({ value: k, label: `${v} (${customers.filter(c => c.user_type === k).length})` })),
                  { value: "custom", label: "Pilih Manual" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setBlastFilter(opt.value)}
                    className={`text-[10px] font-bold uppercase px-3 py-1.5 border transition-colors ${blastFilter === opt.value ? "bg-[#E60000] border-[#E60000] text-white" : "bg-[#1A1A1A] border-[#262626] text-gray-400 hover:border-[#E60000] hover:text-white"}`}
                    data-testid={`blast-filter-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Custom number picker */}
              {blastFilter === "custom" && (
                <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-3 max-h-40 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] text-gray-500 uppercase font-bold">{Object.values(blastCustom).filter(Boolean).length} terpilih</p>
                    <button onClick={selectAllCustom} className="text-[10px] text-[#E60000] hover:underline">Pilih Semua</button>
                  </div>
                  <div className="space-y-1">
                    {customers.map(c => (
                      <button key={c.id} onClick={() => toggleCustom(c.phone)} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#1A1A1A] text-left" data-testid={`blast-pick-${c.id}`}>
                        {blastCustom[c.phone] ? <CheckSquare size={14} className="text-[#E60000] shrink-0" /> : <Square size={14} className="text-gray-600 shrink-0" />}
                        <span className="text-xs text-white flex-1 truncate">{c.name}</span>
                        <span className="text-[10px] text-gray-500 font-mono">{c.phone}</span>
                        <Badge className={`text-[8px] border ${c.user_type === "gold" ? "bg-yellow-900/30 text-yellow-400 border-yellow-700/30" : c.user_type === "platinum" ? "bg-purple-900/30 text-purple-400 border-purple-700/30" : c.user_type === "prioritas" ? "bg-[#E60000]/20 text-[#FF4444] border-[#E60000]/30" : "bg-gray-700/30 text-gray-400 border-gray-600/30"}`}>{c.user_type || "silver"}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Users size={12} className="text-[#E60000]" /> <span className="text-white font-bold">{blastTargets.length}</span> nomor akan menerima pesan</p>
            </div>

            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase">Pesan Broadcast</Label>
              <textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[120px] text-sm focus:border-[#E60000] focus:outline-none" placeholder="Tulis pesan broadcast..." value={blastMsg} onChange={e => setBlastMsg(e.target.value)} data-testid="wa-blast-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-1.5 flex items-center gap-1"><Image size={12} /> Lampiran Gambar</Label>
              <ImageAttach url={blastUrl} onUpload={f => handleUpload(f, u => setBlastUrl(u))} onPaste={u => setBlastUrl(u)} onRemove={() => setBlastUrl("")} uploading={uploading} />
            </div>
            {templates.length > 0 && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-[10px] text-gray-500 uppercase font-bold">Template:</span>
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t, "blast")} className="text-[10px] px-2 py-1 border border-[#262626] text-gray-400 hover:text-[#E60000] hover:border-[#E60000] flex items-center gap-1">
                    {t.image_url && <Image size={10} className="text-green-500" />}{t.name}
                  </button>
                ))}
              </div>
            )}
            <Button onClick={sendBlast} disabled={sending || notConfigured || blastTargets.length === 0} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="wa-blast-btn">
              <Zap size={14} className="mr-2" />{sending ? "Mengirim..." : `Blast ke ${blastTargets.length} Nomor${blastUrl ? " dengan Foto" : ""}`}
            </Button>
          </div>
        </TabsContent>

        {/* === JADWAL === */}
        <TabsContent value="scheduled">
          <div className="bg-[#0F0F0F] border border-[#262626] p-6" data-testid="wa-scheduled">
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-white font-semibold">Pesan Terjadwal & Berulang</p>
                <p className="text-gray-500 text-xs mt-0.5">Pesan dikirim otomatis sesuai jadwal (dicek setiap menit)</p>
              </div>
              <Button onClick={openSchedDialog} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-8 text-xs font-bold uppercase" data-testid="add-sched-btn"><Plus size={14} className="mr-1" /> Buat Jadwal</Button>
            </div>
            {scheduled.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">Belum ada pesan terjadwal</p> : (
              <div className="space-y-2">
                {scheduled.map(s => {
                  const typeLabel = { once: "Sekali", daily: "Harian", weekly: "Mingguan", monthly: "Bulanan" }[s.schedule_type] || s.schedule_type;
                  const filterLabel = s.target_filter === "all" ? "Semua" : s.target_filter === "custom" ? "Manual" : (typeLabels[s.target_filter] || s.target_filter);
                  const nextRun = s.next_run ? new Date(s.next_run) : null;
                  const nextStr = nextRun ? nextRun.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
                  return (
                    <div key={s.id} className={`border p-4 flex items-start gap-4 ${s.is_active ? "bg-[#050505] border-[#262626]" : "bg-[#050505]/50 border-[#1A1A1A] opacity-60"}`} data-testid={`sched-${s.id}`}>
                      {s.image_url && <img src={s.image_url} alt="" className="w-12 h-12 object-cover border border-[#262626] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-white text-sm font-bold">{s.name || "Tanpa Nama"}</p>
                          <Badge className={`text-[8px] uppercase border ${s.schedule_type === "once" ? "bg-blue-900/30 text-blue-400 border-blue-800/30" : "bg-purple-900/30 text-purple-400 border-purple-800/30"}`}>
                            <Repeat size={8} className="mr-0.5" /> {typeLabel}
                          </Badge>
                          <Badge className="text-[8px] bg-[#1A1A1A] border border-[#262626] text-gray-400">{filterLabel}</Badge>
                          {s.image_url && <Badge className="text-[8px] bg-green-900/20 text-green-500 border border-green-800/30"><Image size={8} className="mr-0.5" /> Foto</Badge>}
                        </div>
                        <p className="text-gray-400 text-xs line-clamp-1">{s.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                          <span className="flex items-center gap-1"><CalendarClock size={10} /> Selanjutnya: <span className="text-white font-semibold">{nextStr}</span></span>
                          {s.run_count > 0 && <span>Terkirim {s.run_count}x</span>}
                          {s.last_run && <span>Terakhir: {new Date(s.last_run).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => toggleScheduled(s.id)} className={`p-1.5 transition-colors ${s.is_active ? "text-green-400 hover:text-green-300" : "text-gray-600 hover:text-gray-400"}`} title={s.is_active ? "Nonaktifkan" : "Aktifkan"} data-testid={`toggle-sched-${s.id}`}>
                          <Power size={16} />
                        </button>
                        <button onClick={() => deleteScheduled(s.id)} className="p-1.5 text-gray-600 hover:text-red-400" data-testid={`del-sched-${s.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* === TEMPLATES === */}
        <TabsContent value="templates">
          <div className="bg-[#0F0F0F] border border-[#262626] p-6" data-testid="wa-templates">
            <div className="flex justify-between mb-4">
              <p className="text-white font-semibold">Template Pesan</p>
              <Button onClick={() => { setTplForm({ name: "", message: "", image_url: "" }); setTplDialog(true); }} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-8 text-xs font-bold uppercase" data-testid="add-template-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
            </div>
            {templates.length === 0 ? <p className="text-gray-500 text-sm">Belum ada template</p> : (
              <div className="space-y-2">{templates.map(t => (
                <div key={t.id} className="bg-[#050505] border border-[#262626] p-3 flex items-start gap-3">
                  {t.image_url && <img src={t.image_url} alt="" className="w-12 h-12 object-cover border border-[#262626] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-semibold">{t.name}</p>
                      {t.image_url && <Badge className="bg-green-900/20 text-green-500 border border-green-800/30 text-[8px]"><Image size={8} className="mr-0.5" /> Foto</Badge>}
                    </div>
                    <p className="text-gray-400 text-xs mt-1 whitespace-pre-line line-clamp-2">{t.message}</p>
                  </div>
                  <button onClick={() => deleteTpl(t.id)} className="text-gray-500 hover:text-[#E60000] shrink-0"><Trash2 size={14} /></button>
                </div>
              ))}</div>
            )}
          </div>
        </TabsContent>

        {/* === LOG === */}
        <TabsContent value="log">
          <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="wa-notifications-log">
            <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
              <p className="text-white text-sm font-semibold">Log Notifikasi</p>
              <p className="text-xs text-gray-500">Auto-blast: pesanan baru, pembayaran, POS, refund</p>
            </div>
            {notifications.length === 0 ? <div className="p-8 text-center text-gray-500 text-sm">Belum ada notifikasi terkirim</div> : (
              <div className="max-h-[400px] overflow-auto">
                {notifications.map((n, i) => (
                  <div key={i} className="px-5 py-3 border-b border-[#262626]/50 hover:bg-[#1A1A1A]">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-400">{n.phone}</span>
                        <Badge className={n.success ? "bg-green-900/30 text-green-400 text-[9px]" : "bg-red-900/30 text-red-400 text-[9px]"}>{n.success ? "Terkirim" : "Gagal"}</Badge>
                        <Badge variant="secondary" className="text-[9px] uppercase">{n.event_type}</Badge>
                      </div>
                      <span className="text-[10px] text-gray-500">{new Date(n.created_at).toLocaleString("id-ID")}</span>
                    </div>
                    <p className="text-gray-400 text-xs truncate">{n.message?.substring(0, 120)}...</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={tplDialog} onOpenChange={setTplDialog}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-md rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">Tambah Template</DialogTitle><DialogDescription className="text-gray-400 text-sm">Buat template pesan WhatsApp dengan gambar</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Nama</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={tplForm.name} onChange={e => setTplForm({...tplForm, name: e.target.value})} data-testid="tpl-name-input" /></div>
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Pesan</Label><textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[100px] text-sm focus:border-[#E60000] focus:outline-none" value={tplForm.message} onChange={e => setTplForm({...tplForm, message: e.target.value})} data-testid="tpl-message-input" /></div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-1.5 flex items-center gap-1"><Image size={12} /> Gambar Template</Label>
              <ImageAttach url={tplForm.image_url} onUpload={f => handleUpload(f, u => setTplForm(p => ({...p, image_url: u})))} onPaste={u => setTplForm(p => ({...p, image_url: u}))} onRemove={() => setTplForm(p => ({...p, image_url: ""}))} uploading={uploading} />
            </div>
            <Button onClick={saveTpl} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="tpl-save-btn">Simpan Template</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={schedDialog} onOpenChange={setSchedDialog}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg rounded-none max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2"><CalendarClock size={20} className="text-[#E60000]" /> Buat Jadwal Pesan</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Atur pesan terjadwal atau berulang otomatis</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Nama Jadwal</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" placeholder="Contoh: Promo Mingguan" value={schedForm.name} onChange={e => setSchedForm({...schedForm, name: e.target.value})} data-testid="sched-name" /></div>

            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-2 block">Frekuensi</Label>
              <div className="flex gap-2">
                {[
                  { v: "once", l: "Sekali" },
                  { v: "daily", l: "Harian" },
                  { v: "weekly", l: "Mingguan" },
                  { v: "monthly", l: "Bulanan" },
                ].map(opt => (
                  <button key={opt.v} onClick={() => setSchedForm({...schedForm, schedule_type: opt.v})} className={`flex-1 py-2 text-xs font-bold uppercase border transition-colors ${schedForm.schedule_type === opt.v ? "bg-[#E60000] border-[#E60000] text-white" : "bg-[#1A1A1A] border-[#262626] text-gray-400 hover:border-[#E60000]"}`} data-testid={`sched-type-${opt.v}`}>{opt.l}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase">{schedForm.schedule_type === "once" ? "Tanggal" : "Mulai Tanggal"}</Label>
                <Input type="date" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={schedForm.schedule_date} onChange={e => setSchedForm({...schedForm, schedule_date: e.target.value})} data-testid="sched-date" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase">Jam (WIB)</Label>
                <Input type="time" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={schedForm.schedule_time_val} onChange={e => setSchedForm({...schedForm, schedule_time_val: e.target.value})} data-testid="sched-time" />
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-2 block">Target Penerima</Label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: "all", l: "Semua" },
                  ...Object.entries(typeLabels).map(([k, v]) => ({ v: k, l: v })),
                ].map(opt => (
                  <button key={opt.v} onClick={() => setSchedForm({...schedForm, target_filter: opt.v})} className={`px-3 py-1.5 text-[10px] font-bold uppercase border transition-colors ${schedForm.target_filter === opt.v ? "bg-[#E60000] border-[#E60000] text-white" : "bg-[#1A1A1A] border-[#262626] text-gray-400 hover:border-[#E60000]"}`} data-testid={`sched-target-${opt.v}`}>{opt.l}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase">Pesan</Label>
              <textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[100px] text-sm focus:border-[#E60000] focus:outline-none" placeholder="Tulis pesan..." value={schedForm.message} onChange={e => setSchedForm({...schedForm, message: e.target.value})} data-testid="sched-message" />
            </div>

            {templates.length > 0 && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-[10px] text-gray-500 uppercase font-bold">Template:</span>
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t, "sched")} className="text-[10px] px-2 py-1 border border-[#262626] text-gray-400 hover:text-[#E60000] hover:border-[#E60000] flex items-center gap-1">
                    {t.image_url && <Image size={10} className="text-green-500" />}{t.name}
                  </button>
                ))}
              </div>
            )}

            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase mb-1.5 flex items-center gap-1"><Image size={12} /> Lampiran Gambar</Label>
              <ImageAttach url={schedForm.image_url} onUpload={f => handleUpload(f, u => setSchedForm(p => ({...p, image_url: u})))} onPaste={u => setSchedForm(p => ({...p, image_url: u}))} onRemove={() => setSchedForm(p => ({...p, image_url: ""}))} uploading={uploading} />
            </div>

            <Button onClick={saveScheduled} disabled={sending} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="sched-save-btn">
              <CalendarClock size={14} className="mr-2" /> {sending ? "Menyimpan..." : "Buat Jadwal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
